import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Modal,
  Button,
  Form,
  RadioGroup,
  Radio,
  Input,
  Checkbox,
  CheckboxGroup,
} from 'tea-component';
import useTranslate from '@/pages/opinion/components/FilterCommon/hooks/useTranslate';
import moment from 'moment';
import BubbleCommon, { commonTimeTipsText } from '@op/components/BubbleCommon';
import { throttle } from 'lodash-es';
import { ISavePopupType } from '@opm/components/FilterCommon/types';
import { useHistoryFilterMutation } from '@opm/components/FilterCommon/hooks/useHistoryFilterMutation';
import { saveFilterDateMap, dealwithDate } from '@opm/components/FilterCommon/utils/time';
import { TServerDateType } from '@opm/types/common';
import './index.scss';
const errorText =
  'A setting with the same name already exists, click Save to replace this setting.';
export default function SavePopup({
  close,
  saveOpen,
  filterValue,
  renderConfig,
  onSuccess,
  pagePath,
}: ISavePopupType) {
  const { formatMessage } = useTranslate();
  const [timeType, setTimeType] = useState('quick');
  const [filterName, setFilterName] = useState('');
  const [tipsMessage, setTipsMessage] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<Array<string>>([]);
  const allSelectedFilters = useRef<Array<string>>([]);

  const { loading, checkNameExists, addFilterItem } = useHistoryFilterMutation({
    onAddSuccess: (data: any) => {
      // 保存成功后的回调
      onSuccess?.(data);
    },
    onAddError: (_error: any) => {
      console.error('Failed to add filter:', _error);
    },
    pagePath,
  });

  /**
   * 校验筛选器名称（节流）
   */
  const verifyName = useCallback(
    throttle(async (name: string) => {
      if (!name.trim()) {
        setTipsMessage('');
        return;
      }
      const exists = await checkNameExists(name);
      if (exists) {
        setTipsMessage(errorText);
      } else {
        setTipsMessage('');
      }
    }, 1000),
    [checkNameExists],
  );

  // 时间范围显示
  const timeShowInfo = useMemo(() => {
    const { date_type, start_time, end_time } = filterValue || {};
    const isHourly = date_type === 'hourly';

    if (isHourly) {
      return [start_time, end_time];
    }

    const { formate } = saveFilterDateMap[date_type as TServerDateType] || {};
    return [
      moment(start_time).format(formate),
      moment(end_time).format(formate),
    ];
  }, [filterValue]);

  // 快速时间选择显示
  const quickShowTime = useMemo(() => {
    const { date_type, start_time, end_time } = filterValue || {};

    if (date_type !== 'hourly') {
      const { before_text, suffix, current } =
        saveFilterDateMap[date_type as TServerDateType] || {};
      const value = dealwithDate(start_time, end_time, date_type);
      let showText = current;
      if (value > 0) {
        showText = `${formatMessage({ id: 'Last' })} ${value} ${formatMessage({
          id: suffix,
        })}`;
      } else if (value < 0) {
        showText = formatMessage({ id: before_text });
      }
      return showText;
    }
    return 'Today';
  }, [filterValue, formatMessage]);

  // 初始化选中的筛选器
  useEffect(() => {
    const allSelectedKey: Array<string> = [];
    renderConfig.forEach((item) => {
      allSelectedKey.push(item.key);
    });
    allSelectedFilters.current = allSelectedKey;
    setSelectedFilters(allSelectedKey);
  }, [renderConfig]);

  const getValueByKey = (
    key: string | string[] | Record<string, string>,
  ): any => {
    if (typeof key === 'string') {
      return filterValue?.[key];
    }
    if (Array.isArray(key)) {
      return key.map((k) => filterValue?.[k]);
    }
    if (typeof key === 'object') {
      const result: Record<string, any> = {};
      Object.keys(key).forEach((k) => {
        result[k] = filterValue?.[key[k]];
      });
      return result;
    }
    return undefined;
  };

  const handleConfirm = () => {
    if (!filterName.trim()) {
      setTipsMessage('Please enter a name');
      return;
    }

    const filterCond: any = {};

    // 根据 selectedFilters 和 renderConfig 获取对应的 filterValue 值
    selectedFilters.forEach((selectedKey) => {
      const configItem = renderConfig.find(
        (item: any) => item.key === selectedKey,
      );
      const { valueKeys } = configItem?.props ?? {};
      if (valueKeys) {
        // 处理不同类型的 valueKeys
        if (typeof valueKeys === 'string') {
          if (filterValue?.[valueKeys] !== undefined) {
            filterCond[valueKeys] = filterValue[valueKeys];
          }
        } else if (Array.isArray(valueKeys)) {
          valueKeys.forEach((key) => {
            if (filterValue?.[key] !== undefined) {
              filterCond[key] = filterValue[key];
            }
          });
        } else if (typeof valueKeys === 'object') {
          // 处理对象类型的 valueKeys
          Object.keys(valueKeys).forEach((k) => {
            const dataKey = valueKeys[k];
            if (filterValue?.[dataKey] !== undefined) {
              filterCond[k] = filterValue[dataKey];
            }
          });
        }
      }
    });

    if (filterValue) {
      const { start_time, end_time, date_type } = filterValue;

      Object.assign(filterCond, {
        start_time,
        end_time,
        date_type,
        ...(timeType === 'quick' && {
          time_value: dealwithDate(start_time, end_time, date_type),
        }),
      });
    }

    addFilterItem(filterName, filterCond);
  };

  return (
    <Modal
      visible={saveOpen}
      size="l"
      caption={formatMessage({ id: 'Save Filter Setting' })}
      className="dbom-save-model"
      onClose={close}
    >
      <Modal.Body>
        <div className="dbom-model-text">
          {formatMessage({
            id: 'Saved settings can only be used in the filters on this page.',
          })}
        </div>
        <Form className="dbom-model-form">
          <Form.Item
            status={tipsMessage ? 'error' : undefined}
            label={formatMessage({ id: 'Setting Name' })}
            showStatusIcon={false}
            message={tipsMessage ? formatMessage(tipsMessage) : ''}
          >
            <Input
              placeholder={formatMessage({ id: 'Please enter a name' })}
              className="dbom-model-input"
              value={filterName}
              onChange={(value) => {
                setFilterName(value);
                verifyName(value);
              }}
            />
          </Form.Item>
          <Form.Item
            label={
              <div className="dbom-tab-flex">
                <BubbleCommon
                  placement="top"
                  tipsMap={commonTimeTipsText(
                    timeShowInfo,
                    quickShowTime || 'Today',
                  )}
                >
                  {formatMessage({ id: 'Time Settings' })}
                </BubbleCommon>
              </div>
            }
            showStatusIcon={false}
          >
            <RadioGroup
              className="dbom-model-group"
              value={timeType}
              onChange={(value) => setTimeType(value)}
            >
              <Radio name="default" className="dbom-model-group-item">
                <span className="dbom-model-form-text">
                  {formatMessage({ id: 'Start&End Date' })}
                </span>
                <span>{timeShowInfo.join(' ~ ')}</span>
              </Radio>
              <Radio name="quick">
                <span className="dbom-model-form-text">
                  {formatMessage({ id: 'Date Duration' })}
                </span>
                <span>{quickShowTime}</span>
              </Radio>
            </RadioGroup>
          </Form.Item>
          <Form.Item
            label={formatMessage({ id: 'Other Settings' })}
            showStatusIcon={false}
          >
            <CheckboxGroup
              className="dbom-model-form-group"
              value={selectedFilters}
              onChange={(value) => setSelectedFilters(value)}
            >
              {/* 渲染 ButtonComponent */}
              {renderConfig.map((item) => {
                if (!item?.ButtonComponent) return null;
                // TODO: 多Key的情况没有处理过，后续出现了，得多加注意
                const value = getValueByKey(item.props.valueKeys);

                return (
                  <div key={item.key} className="dbom-model-form-checkbox">
                    <Checkbox name={item.key}>
                      <item.ButtonComponent {...item.props} value={value} />
                    </Checkbox>
                  </div>
                );
              })}
            </CheckboxGroup>
          </Form.Item>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          disabled={!filterName.trim()}
          onClick={handleConfirm}
          loading={loading}
        >
          {formatMessage({ id: 'Confirm' })}
        </Button>
        <Button type="weak" onClick={close}>
          {formatMessage({ id: 'Cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
